<template>
  <div id="filter-by-tags">
    <div
      class="main-title"
      @click="() => _updateStatus(true)"
      @mouseover="filtersHovered = true"
      @mouseout="filtersHovered = false"
      :class="{ ['inactive']: isContactsLoading, [extraClass]: true }"
    >
      <img :src="_filterIcon" alt="filter" />
      <span class="main-title__tags">{{ getFilterTitle }}</span>
    </div>
    <div v-show="isOpened">
      <div id="backdrop" @click.self="() => _updateStatus(false)"></div>
      <div class="filter-by-tags__actions" ref="filter">
        <VInput
          name="filterTags"
          icon="search"
          iconFABase="fal"
          iconPosition="right"
          placeholder="Find Your Tag"
          extraClass="search-tags-input"
          :value="searchString"
          @input="onFilter"
        ></VInput>
        <WithLoader :isLoading="isFilteredTagsLoading">
          <span class="tip">Click on tag to add or remove from selected</span>
          <div class="tags__outer">
            <div class="tags__wrapper">
              <div class="tags__title__wrapper">
                <div class="tags__title">Filter By Tags</div>
              </div>
              <div class="tags">
                <div
                  v-for="tag in tags"
                  :key="tag.id"
                  class="tag-wrapper__fetched"
                  @click="onFetchedTag(tag)"
                  @mouseover="selectArrorHovered = true"
                  @mouseleave="selectArrorHovered = false"
                >
                  <span class="tag tag__fetched">
                    {{ tag.name }}
                  </span>
                </div>
              </div>
            </div>
            <div class="arrows-outer">
              <span
                class="arrow-wrapper"
                :class="{ ['active']: selectArrorHovered }"
              >
                <i class="fas fa-arrow-right fa-lg"></i>
              </span>
              <span
                class="arrow-wrapper"
                :class="{
                  ['active']: unselectArrorHovered
                }"
              >
                <i class="fas fa-arrow-left fa-lg"></i>
              </span>
            </div>
            <div class="tags__wrapper">
              <div class="tags__title__wrapper">
                <span class="tags__title">
                  Selected Tags
                </span>
                <span
                  v-show="selectedTags.length"
                  class="tags__remove-all"
                  @click="preRemoveTags(selectedTags)"
                  >Remove All Tags</span
                >
              </div>
              <span v-show="!this.selectedTags.length" class="empty-tags"
                >No Tags</span
              >
              <div v-show="selectedTags.length" class="tags">
                <div
                  v-for="tag in selectedTags"
                  :key="tag.id"
                  class="tag-wrapper__selected"
                  :class="{ ['warning']: tag.id === removeHovered }"
                >
                  <span
                    class="tag tag__selected"
                    @mouseover="unselectArrorHovered = true"
                    @mouseleave="unselectArrorHovered = false"
                    @click="onSelectedTag(tag)"
                  >
                    {{ tag.name }}
                  </span>
                  <span
                    class="tag__remove"
                    @mouseover="removeHovered = tag.id"
                    @mouseleave="removeHovered = null"
                    @click="preRemoveTags([tag])"
                    >Remove</span
                  >
                </div>
              </div>
            </div>
          </div>
        </WithLoader>
      </div>
    </div>
    <v-modal
      v-if="isModalOpened"
      version="new"
      title="Are You Sure?"
      :actions="getActions"
    >
      <div class="modal-text">
        The tag(s)
        <span class="tags-to-remove">{{ this.getTagsToRemoveLabels }}</span>
        will be removed from the selected contacts and by itself.
        <div class="checkbox-wrapper">
          <VCheckbox
            name="removeMixed"
            label="Do not remove contacts with multiple tags"
            :checked="!removeContactsWithMultiTags"
            @change="removeContactsWithMultiTags = !removeContactsWithMultiTags"
            extraClass="dont-remove-checkbox"
          ></VCheckbox>
          <VHint
            :text="hintText"
            extraClass="integrations-hint"
            position="up"
            size="fit"
            preLine
          />
        </div>
      </div>
    </v-modal>
  </div>
</template>

<script>
import Vue from "vue";
import VueTypes from "vue-types";
import { mapGetters, mapActions } from "vuex";
import { debounce } from "lodash";

//components
import WithLoader from "@/components/WithLoader";
import VInput from "@/components/VInput";
import VModal from "@/components/VModal";
import VCheckbox from "@/components/VCheckbox";

//assets
import filterIcon from "@/assets/images/filter-new.svg";
import filterIconActive from "@/assets/images/filter-new-active.svg";

//mixins
import withToasted from "@/mixins/withToasted";

export default Vue.component("FilterByTags", {
  props: {
    // tags: VueTypes.array
    extraClass: VueTypes.string,
    isOpened: VueTypes.bool.def(false),
    onStatusUpdate: VueTypes.func
  },

  components: {
    VInput,
    WithLoader,
    VModal,
    VCheckbox
  },

  mixins: [withToasted],

  data() {
    return {
      filtersHovered: false,
      searchString: "",
      tags: [],
      selectedTags: [],
      isModalOpened: false,
      tagsToRemove: [],
      removeContactsWithMultiTags: true,
      selectArrorHovered: false,
      unselectArrorHovered: false,
      //hovered tag for removing
      removeHovered: null,

      hintText:
        "Leave contacts which have more than 1 tag.\n" +
        " By default, every contact which contains the selected tag \n" +
        " will be deleted."
    };
  },

  computed: {
    _filterIcon() {
      return this.filtersHovered ? filterIconActive : filterIcon;
    },

    getFilterTitle() {
      return this.selectedTags.length
        ? this.selectedTags.map(tag => tag.name).join(", ")
        : "Filter";
    },

    getTagsToRemoveLabels() {
      if (this.tagsToRemove.length) {
        return this.tagsToRemove.map(tag => tag.name).join(", ");
      } else {
        return "";
      }
    },

    // for modal
    getActions() {
      return [
        {
          label: "Cancel",
          value: "cancel",
          noBorder: true,
          size: "sm",
          inverted: true,
          click: () => this.onCancel()
        },
        {
          label: "Remove",
          value: "remove",
          type: "primary",
          size: "wide",
          click: () => { 
            this.removeContactsWithMultiTags = true;
            this._deleteContactsByTags(this.tagsToRemove) }
          // disabled: !this.isValid || this.isLoading
        }
      ];
    },

    ...mapGetters("tags", {
      isFilteredTagsLoading: "isFilteredTagsLoading",
      tagsFromStore: "tags"
    }),
    ...mapGetters("contacts", { isContactsLoading: "isLoading" })
  },

  methods: {
    _updateStatus(status) {
      this.onStatusUpdate && this.onStatusUpdate(status);
    },

    onFilter: debounce(function s(val) {
      this.searchString = val;
      this.fetchFilteredTagsList({
        search: val,
        onSuccess: resp => {
          // check is one of the fetched tags
          // already is in selected tags
          // if true then remove it from
          // fetched tags response

          let filteredTags = resp.filter(tag => {
            if (this.selectedTags.length) {
              return this.selectedTags.every(selectedTag => {
                return selectedTag.id !== tag.id;
              });
            } else {
              return true;
            }
          });
          this.tags = filteredTags;
        }
      });
    }, 400),

    onFetchedTag(tag) {
      const tagsIds = this.tags.length ? this.tags.map(tag => tag.id) : [];

      const indexOfTag = tagsIds.indexOf(tag.id);
      this.tags.splice(indexOfTag, 1);

      this.selectedTags.push(tag);

      if (!this.tags.length) {
        this.selectArrorHovered = false;
      }

      this.updateSelectedTags({ tags: this.selectedTags });

      this.$emit("change", this.selectedTags);
    },

    onSelectedTag(unselectedTag) {
      this.selectedTags = this.selectedTags.filter(
        tag => unselectedTag.id !== tag.id
      );

      if (!this.selectedTags.length) {
        this.unselectArrorHovered = false;
      }
      this.updateSelectedTags({ tags: this.selectedTags });
      this.tags.unshift(unselectedTag);
      this.$emit("change", this.selectedTags);
    },

    preRemoveTags(tags) {
      if (this.selectedTags.length) {
        this.tagsToRemove = tags;
        this.isModalOpened = true;
      }
    },

    removeTags() {
      if (this.selectedTags.length) {
        this.updateSelectedTags({ tags: [] });
        this.selectedTags = [];
      }
    },

    _deleteContactsByTags(tags) {
      let tagsIdsArr = tags.map(tag => tag.id);
      let tagsIdsStr = tagsIdsArr.join(",");
      this.isModalOpened = false;
      this.deleteContactsByTags({
        tagsQueryString: tagsIdsStr,
        crossedDeletion: this.removeContactsWithMultiTags,
        onSuccess: () => {
          this.deleteTags({
            idsArr: tagsIdsArr,
            onSuccess: () => {
              this.tagsToRemove = [];
              this.selectedTags = this.tagsFromStore("selectedTags");
              this.isModalOpened = false;
              this.toast(
                `Tags ${this.getTagsToRemoveLabels} has been deleted succesfully!`
              );
            }
          });
        }
      });
    },

    isTagChecked(id) {
      if (this.selectedTags.length) {
        return this.selectedTags.find(tag => tag.id === id);
      }
      return false;
    },

    onCancel() {
      this.isModalOpened = false;
      this.tagsToRemove = [];
    },

    ...mapActions("tags", [
      "fetchFilteredTagsList",
      "updateSelectedTags",
      "deleteTags"
    ]),
    ...mapActions("contacts", ["deleteContactsByTags"])
  },

  mounted() {
    this.fetchFilteredTagsList({
      onSuccess: resp => {
        this.tags = resp;
      }
    });
  }
});
</script>

<style lang="scss" scoped>
$ns: "filter-by-tags";

#filter-by-tags {
  position: relative;
  font-size: 14px;
  font-family: Lato-Regular, sans-serif;
  color: $main-text-color;
}

.#{$ns}__actions {
  position: absolute;
  z-index: 6;
  background-color: white;
  box-shadow: 3px 3px 25px rgba(0, 0, 0, 0.25);
  border-radius: 5px;
  padding: 10px;
  width: 570px;
}

#backdrop {
  width: 100vw;
  height: 100vh;
  position: fixed;
  top: 0px;
  left: 0px;
}

.tip {
  font-family: "Lato-Light", sans-serif;
  font-style: italic;
  color: $secondary-text-color;
}

.main-title {
  display: flex;
  width: 120px;
  align-items: center;
  font-size: 15px;
  color: $primary-grey;
  cursor: pointer;
  &:hover {
    color: $primary-color;
  }
  &.inactive {
    cursor: auto;
    pointer-events: none;
  }

  img {
    margin-right: 7px;
  }
}

.main-title__tags {
  @include textCropEllipsis();
}

.search-tags-input {
  padding-bottom: 10px;
}

.tags__outer {
  padding-top: 15px;
  display: grid;
  grid-auto-flow: column;
  grid-template-columns: 1fr min-content 1fr;
  grid-gap: 15px;
  font-family: Lato-Regular, sans-serif;
}

.tags__wrapper {
  /* overflow-x: hidden; */
  min-height: 50px;
  border: 1px solid $tertiary-grey;
  border-radius: 5px;
}

.tags__title__wrapper {
  display: flex;
  justify-content: space-between;
  font-size: 12px;
  padding: 7px 10px 10px;
}

.tags__remove-all {
  color: $primary-color;
  cursor: pointer;
  display: inline-block;
  height: fit-content;
}

.tags__title {
  font-size: 12px;
  font-family: Lato-Bold, sans-serif;
}

.tags {
  @include customScrollbar();
  display: grid;
  grid-template-columns: 100%;
  grid-auto-rows: 30px;
  height: 100%;
  max-height: 190px;
  overflow-y: auto;
}

.tag-wrapper__fetched,
.tag-wrapper__selected {
  display: grid;
  grid-auto-flow: column;
  align-items: center;
  grid-gap: 7px;
  padding: 0 10px;
  cursor: pointer;
  &:hover {
    .tag {
      color: white;
    }
    background-color: $primary-color;
  }
}

.tag-wrapper__fetched {
  grid-template-columns: 1fr min-content;
}

.tag-wrapper__selected {
  grid-template-columns: 1fr min-content;
  &.warning {
    background: $danger-color;
  }
}

.tag-wrapper__selected:hover .tag__remove {
  display: inline-block;
}

.arrows-outer {
  display: grid;
  grid-auto-rows: min-content;
  align-content: center;
  grid-gap: 10px;
}

.arrow-wrapper {
  color: $tertiary-grey;
  &.active {
    color: $primary-color;
  }
}

.tag {
  overflow: hidden;
  text-overflow: ellipsis;
}

.tag__fetched {
  color: $tertiary-grey;
}

.tag__remove {
  color: white;
  display: none;
  justify-self: end;
}

.empty-tags {
  color: $primary-grey;
  font-size: 14px;
  margin-left: 10px;
}
</style>

<style lang="scss" scoped>
.modal-text {
  font-size: 18px;
  color: $tertiary-text-color;
  white-space: initial;
  line-height: 1.4;
  text-align: left;
}
.tags-to-remove {
  color: $main-text-color;
}

.checkbox-wrapper {
  display: flex;
  align-items: center;
  padding-top: 15px;
}

.dont-remove-checkbox {
  background-color: transparent;
}
</style>
